<template>
  <div
    class="story-wrapper"
    :class="[ story.theme || '' ]"
    :style="`background-image: url(${story.defaultBackground || ''}); background-color: ${story.defaultBackgroundColor || ''}`"
  >
    <div class="hamburger" ref="hamburger" @click="toggleNav(!navShown)">
      <div class="hitbox" />
      <div class="icon" :class="{ close: navShown }">
        <div />
        <div />
        <div />
      </div>
    </div>

    <div
      class="story-nav fullscreen-overlay"
      :class="{ shown: navShown, started: storyStarted }"
    >
      <div class="text-container">
        <div class="left">
          <div class="story-reset-container" @click="resetStory()">
            <h1 v-html="story.title">
              <span v-if="story.id === 'college-football'">:</span>
            </h1>
            <div v-if="story.subtitle" class="subtitle-container">
              <div
                v-for="(part, index) in story.subtitle"
                :key="`subtitle-part-${index}`"
                class="nav-subtitle"
              >
                <h2 v-html="part"></h2>
              </div>
            </div>
          </div>
          <div class="story-outline">
            <div
              v-for="(section, i) in story.sections"
              :key="`section-outline-${i}`"
              class="outline-section"
            >
              <h2 @click="goToSlide(calcSlideIndex(i))" v-html="section.title" :class="{ hidden: section.inOutline === false }"></h2>

              <ul v-if="section.beats">
                <li
                  v-for="(beat, j) in section.beats"
                  :key="`beat-outline-${i}-${j}`"
                  :class="{ active: currentSlide === calcSlideIndex(i, j), hidden: beat.inOutline === false, indent: beat.subSection }"
                  :data-index="calcSlideIndex(i, j)"
                  @click="goToSlide(calcSlideIndex(i, j))"
                >
                  {{ beat.title }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="logo-container">
          <div>
            <button class="logout" @click="logout">
              Logout
            </button>
          </div>

          <router-link to="/stories">
            <img v-if="story.theme === 'dark-theme' && story.id !== 'world-cup'" src="/images/FOXLOGO_WEB_White_Small.png" alt="Fox Sports Logo">
            <img v-else src="/images/FOXLOGO_WEB_Black_Small.png" alt="Fox Sports Logo">
            <h2>Strategy and Analytics</h2>
            <h3>Internal Use Only</h3>
          </router-link>
        </div>
      </div>
    </div>

    <div
      v-if="!story.noCover"
      :class="{ hidden: coverHidden }"
      class="story-cover fullscreen-overlay"
      id="storyCover"
      ref="storyCover"
      :style="{ 'background-image': story.cover && story.cover.background ? `url(${getBgImage(story.cover)})` : '' }"
    >

      <img v-if="!story.hideCoverLogo && storyLoaded" class="fs-logo" src="/images/FOXLOGO_WEB_Black_Small.png" alt="Fox Sports">

      <div class="center">
        <template v-if="story.textTitle">
          <h1 v-html="story.titleAlt || story.title"></h1>
          <h2 v-if="story.subtitle">
            <span v-for="(segment, index) in story.subtitle" :key="`subtitle-segment-${index}`" v-html="segment">
            </span>            
          </h2>
        </template>
        <template v-else>
          <img
            v-if="aspect === 'landscape'"
            :src="story.cover && story.cover.center ? story.cover.center : ''"
            class="cover-image"
            id="storyCoverCenter"
          >
          <img
            v-else-if="story.cover && story.cover.center"
            :src="story.cover && story.cover.centerMobile ? story.cover.centerMobile : story.cover.center"
            class="cover-image-mobile"
            id="storyCoverCenter"
          >
        </template>
      </div>

      <div v-if="storyLoaded" class="start-button" @click="clickStartButton">
        <img :src="`/images/${playFile}`" alt="Begin Story">
        <p>Begin</p>
      </div>
    </div>

    <div v-if="storyBeats.length > 0" class="story-container">
      <VueSlickCarousel
        :infinite="false"
        :dots="false"
        :arrows="false"
        :fade="useFade"
        ref="storyCarousel"
        @init="carouselReady"
        @reInit="carouselReInit"
        @afterChange="afterChange"
        @beforeChange="beforeChange"
      >
        <div
          v-for="(beat, index) in storyBeats"
          :key="`slide-${index}`"
          :class="[beat.type || 'default', { 'loaded': beat.loaded }, beat.customClass || '']"
          class="story-beat"
          :ref="`beat-${index}`"
          :style="{
            'background-image': getSlideBg(beat),
            'background-color' : beat.backgroundColor ? beat.backgroundColor : beat.type === 'quote' ? quoteBgColor : ''
          }"
        >
          <div class="background-cover"></div>
          <!-- Thesis -->
          <div
            v-if="beat.type === 'thesis'"
            class="feature-animation"
            :ref="`feature-animation-${index}`"
            :data-animation="aspect === 'landscape' ? (beat.animation || '') : (beat.animationMobile || '')"
          >
          </div>

          <div
            v-if="beat.type === 'video' && beat.overlay"
            class="video-overlay"
            :class="{ hidden: beat.overlay.fadeOut && clock > beat.overlay.fadeOut }"
          >
            <div class="text-container">
              <h2 v-if="beat.overlay.title" v-html="mobileMode && beat.overlay.titleMobile ? beat.overlay.titleMobile : beat.overlay.title"></h2>
              <p v-if="beat.overlay.body">{{ beat.overlay.body }}</p>
            </div>
          </div>

          <!-- General Background Video -->
          <video
            v-if="beat.backgroundVideo && slideWithinRange(index)"
            :class="[
              getVideoClass(beat.backgroundVideo),
              beat.letterbox ? 'letterbox' : '',
              beat.backgroundVideo.ultrawide ? 'ultrawide' : '',
              (beat.vidFadeOut && clock > beat.vidFadeOut) || (beat.vidFadeOut && currentSlide !== index) ? 'faded' : ''
            ]"
            :src="getBgVideo(beat.backgroundVideo)"
            :data-vid-id="index"
            :ref="`video-${index}`"
            :style="beat.backgroundVideo.opacity ? `opacity: ${beat.backgroundVideo.opacity}` : ''"
            preload="auto"
            playsinline="true"
            webkit-playsinline="true"
            class="fullscreen"
          />

          <div v-if="beat.overlayVideo" class="overlay-video-container">
            <video
              :src="beat.overlayVideo.landscape"
              class="overlay-video"
              autoplay
              loop
              muted
              webkit-playsinline="true"
            />
          </div>

          <div
            v-if="beat.type === 'section-title'"
            class="section-parts"
            :class="beat.cover && beat.cover.align || ''"
          >
            <template v-if="aspect === 'landscape' || !beat.coverMobile">
              <div
                v-if="beat.cover"
                class="layers"
                :class="{ 'moving' : index == targetSlide }"
              >
                <div
                  class="middle"
                  :style="{ 'background-image': `url(${beat.cover.middle || ''})` }"
                >
                </div>

                <div class="front" :style="{ 'background-image': `url(${beat.cover.front || ''})` }"></div>
              </div>
            </template>

            <div
              v-if="aspect !== 'landscape' && beat.coverMobile"
              class="layers"
              :class="{ 'moving' : index == targetSlide }"
            >
              <div
                class="middle"
                :style="{ 'background-image': `url(${beat.coverMobile.middle || ''})` }"
              >
              </div>

              <div class="front" :style="{ 'background-image' : `url(${beat.coverMobile.front || ''})` }"></div>
            </div>
            
            <div class="title" :class="{ visible : index == targetSlide }">
              <!-- STORY SPECIFIC -->
              <template v-if="story.id === 'college-football'">
                <img class="rip-full" src="/images/rip_full.png" alt="Ripped paper">
                <img class="rip-partial" src="/images/rip_partial.png" alt="Ripped paper">
              </template>

              <h2 v-if="beat.subtitle" v-html="beat.subtitle"></h2>
              <h1 v-html="getSectionTitle(beat)"></h1>
            </div>
          </div>

          <InfographicFullscreen v-if="beat.type === 'infographic-fullscreen'" :beatData="beat" />

          <SplitContent v-if="beat.type === 'split-content'" :beatData="beat" :visible="currentSlide == index" />

          <TwoByTwo v-if="beat.type === 'two-by-two'" :beatData="beat" :visible="currentSlide == index" />

          <!-- Max Width Container -->
          <div
            class="container"
            :style="story.id === 'betting' && (beat.type === 'question' || beat.type === 'image') ? `background-image: url(images/background-grain_INVERT.png)` : ''"
          >

            <!-- TEXT ONLY SLIDES -->
            <div v-if="beat.type === 'text'" class="text-container">
              <template v-if="beat.copy && currentSlide == index">
                <div v-for="(segment, i) in getCopy(beat.copy)" :key="`segment-${index}-${i}`" class="segment-container">
                  <template v-if="beat.typed">
                    <TypedText
                      v-if="i == currentCopySegment"
                      class="copy-segment shown"
                      :text="beat.copy[i]"
                      :number="index"
                    >
                    </TypedText>
                  </template>
                  <template v-else-if="beat.slideUp">
                    <SlideUpText
                      v-if="i == currentCopySegment"
                      class="copy-segment shown"
                      :text="(aspect === 'portrait' && beat.copyMobile) ? beat.copyMobile[i] : beat.copy[i]"
                      :delays="beat.copyDelays || []"
                    />
                  </template>

                  <div v-else-if="beat.animation == 'fade-drop'">
                    <FadeDropText
                      class="copy-segment shown"
                      :text="(aspect === 'portrait' && beat.copyMobile) ? beat.copyMobile : beat.copy"
                      :clock="clock"
                      :duration="beat.duration"
                    />
                  </div>

                  <div v-else class="copy-segment shown" :number="0" v-html="beat.copy"></div>
                </div>
              </template>
            </div>

            <!-- Image -->
            <div v-if="beat.type === 'image'" class="image-container">
              <img v-if="beat.src" :src="getImage(beat.src)" :alt="beat.alt || 'Image description'">
            </div>

            <Editorial v-if="beat.type === 'editorial'" :beatData="beat" :visible="currentSlide == index" />
            <TextAndMedia
              v-if="beat.type === 'text-and-media'"
              :beatData="beat"
              :visible="currentSlide == index"
              :mobileMode="aspect == 'portrait'"
            />

            <!-- Quote -->
            <div v-if="beat.type === 'quote'" class="quote-container">
              <div class="vid-container">
                <video
                  v-if="beat.quoteVideo && slideWithinRange(index)"
                  :class="getVideoClass(beat.quoteVideo)"
                  :src="getBgVideo(beat.quoteVideo)"
                  :data-vid-id="index"
                  :ref="`video-${index}`"
                  preload="auto"
                  playsinline="true"
                  webkit-playsinline="true"
                />
              </div>
              
              <div class="quote-lower-third">
                <div
                  v-if="beat.lowerThird"
                  class="lower-third"
                  :ref="`lower-third-${index}`"
                  :data-animation="beat.lowerThird"
                />
              </div>
            </div>

            <!-- Infographic -->
            <div v-if="beat.type === 'infographic'" class="side-by-side">
              <div class="left" :class="{ hidden: infographicStarted && index == currentSlide }">
                <div
                  class="copy"
                  :class="{ 'visible' : index == currentSlide }"
                  :ref="`copy-${index}`"
                >
                  <div
                    v-for="(segment, i) in beat.copy"
                    class="copy-segment"
                    :class="{ 'shown' : i == currentCopySegment }"
                    :key="`segment-${index}-${i}`"
                  >
                    <span v-html="beat.copy[i]">
                    </span>
                    <br><br>
                  </div>
                  
                </div>
              </div>
              <div class="right">
                <div
                  v-if="beat.infographic"
                  class="infographic-container"
                  :ref="`infographic-${index}`"
                  :data-animation="beat.infographic"
                />
              </div>
            </div>

            <!-- Infographic Full -->
            <div v-if="beat.type === 'infographic-full'" class="infographic-full-container">
              <div
                class="feature-animation"
                :ref="`feature-animation-${index}`"
                :data-animation="getTargetInfographic(beat.infographic)"
              >
              </div>
              
              <TypedText v-if="beat.copy && currentSlideInitialized && (!beat.copyDelay || clock > beat.copyDelay)" :text="beat.copy" />
            </div>
            
            <!-- Testimonial -->
            <div v-if="beat.type === 'testimonial'" class="lower-third-container">
              <div
                v-if="beat.lowerThird"
                class="lower-third"
                :ref="`lower-third-${index}`"
                :data-animation="mobileLowerThirds ? beat.lowerThirdMobile : beat.lowerThird"
              />
            </div>

            <!-- Question -->
            <div v-if="beat.type === 'question'" class="question-container" :class="{ answered: showingResults[currentSlide] === true }">
              <div class="split">
                <div class="left">
                  <img class="header-image" src="/images/question_text_INVERT.png" alt="Question">
                  <p v-if="!beat.question && beat.questionRendered" class="question">{{ beat.question }}</p>
                  <img v-if="beat.questionRendered" class="question-render" :src="beat.questionRendered" :alt="beat.question || 'Image of question text.'">
                  <img v-if="beat.questionImage" class="question-image" :src="beat.questionImage" alt="Question Image">
                  <div class="answers">
                    <ol>
                      <li
                        v-for="(answer, i) in beat.answers"
                        :key="`answer-${i}`"
                        :class="{
                          selected: chosenAnswers[i] === true,
                          correct: answersSubmitted[currentSlide] && (beat.correct === i || (Array.isArray(beat.correct) && beat.correct.includes(i))),
                          incorrect: answersSubmitted[currentSlide] && chosenAnswers[i] === true && ((!Array.isArray(beat.correct) && beat.correct !== i) || (Array.isArray(beat.correct) && beat.correct.includes(i) === false))
                        }"
                        @click="chooseAnswer(i)"
                      >
                        {{ answer }}
                      </li>
                    </ol>
                    <button @click="submitAnswers()">Submit</button>
                  </div>
                </div>
                <div class="right">
                  <img class="header-image" src="/images/results_text_INVERT.png" alt="Results">
                  <ul class="results-list" :class="`results-${index}`" v-if="beat.results">
                    <li v-for="(line, i) in beat.results.lines" :key="`line-${i}`" class="type-on" v-html="line"></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </VueSlickCarousel>
    </div>

    <div
      class="bg-vid-cover"
      :class="{ visible: showTransitionStill }"
    />

    <!-- Story Navigation UI -->
    <div class="ui-container">
      <div
        class="audio-notification"
        :class="{ visible: !viewedAudioNotification }"
        @click="dismissAudioNotification"
      >
        <div class="circle" />
        <div class="content">
          <p>This experience</p>
          <p>involves <span>sound</span></p>
          <button><span>It's On</span></button>
        </div>
      </div>
      <h4
        :class="{ hidden: slideTitleHidden }"
        class="slide-title"
        ref="slideTitle">
      </h4>

      <transition name="fade">
        <img :src="currentWidget" class="section-widget" :class="{ visible: showingWidget }" alt="Section Widget">
      </transition>

      <transition name="fade-fast">
        <h2
          v-if="story.persistentSectionTitles"
          v-html="currentSection"
          :key="currentSection"
          class="persistent-section-title"
          :class="{ visible: showingPersistentSectionTitle }"
          alt="Section Widget"
        />
      </transition>
      
      <div class="story-arrows" :class="{ minimal: minimalArrows }">
        <div class="story-arrow next-arrow" :class="{ fullWidth: !storyStarted }" @click="nextSlide">
          <div class="hitbox"></div>
          <img v-if="story.theme === 'dark-theme' || story.theme === 'white-ui'" src="/images/right_white.svg" alt="Next Slide">
          <img v-else src="/images/right.svg" alt="Next Slide">
        </div>
        <div class="story-arrow prev-arrow" @click="prevSlide">
          <div class="hitbox"></div>
          <img
            v-if="story.theme === 'dark-theme' || story.theme === 'white-ui'"
            src="/images/left_white.svg"
            alt="Previous Slide"
          >
          <img v-else src="/images/left.svg" alt="Previous Slide">
        </div>
      </div>

      <div class="play-button" @click="storyPlaying ? pauseStory() : resumeStory()" >
        <img v-if="!storyPlaying" :src="story.theme === 'dark-theme' || story.theme === 'white-ui' ? '/images/play_white.svg': '/images/play.svg'" alt="Play Story">
        <img v-if="storyPlaying" :src="story.theme === 'dark-theme' || story.theme === 'white-ui' ? '/images/pause_white.svg': '/images/pause.svg'" alt="Pause Story">
      </div>

      <div class="story-progress-bar">
        <div class="loaders-container">
          <div
            v-for="(beat, index) in progressBarSegments"
            :key="`loader-${index}`"
            :style="{ 'width' : `${progressBarWidths[index]}%` }"
            class="loader-container"
          >
            <button @click="beat.jumpPoint ? jumpToPoint(beat.rootIndex, beat.jumpIndex) : goToSlide(beat.rootIndex)">
              <div
                class="loader"
                :style="[{ 'width' : `${loaderPercent(index)}%` }, progressBarColors[index] ? { 'background-color' : progressBarColors[index] } : '']"
              />
            </button>
          </div>
        </div>
        
        <div class="section-titles" :class="{ hidden: story.hideProgressBarTitles }">
          <h4
            v-for="(section, index) in progressBarTitles"
            :key="`section-title-${index}`"
            :style="{ 'width' : `${titleWidths[index]}%` }"
            v-html="section"
          >
          </h4>
        </div>
      </div>

      <div class="replay-button" :class="{ visible: storyFinished }" @click="resetStory()">
        <img :src="`/images/${replayFile}`" alt="Replay story">
        <p>Replay</p>
      </div>

      <div v-if="waitingForVideo" class="load-overlay">
        <div class="content" @click="resumeStory">
          <v-progress-circular
            size="80"
            indeterminate
            :color="loaderColor"
          />
          <div :class="{ visible: showReload }" class="text">
            <p>Trouble Loading Video?</p>
            <p class="reload">Tap Here to Reload</p>
          </div>          
        </div>
      </div>
    </div>

    <div v-if="loading" class="global-loader">
      <v-progress-circular size="80" indeterminate color="white" />
    </div>
  </div>
</template>

<script>
import VueSlickCarousel from 'vue-slick-carousel'
import TypedText from '@/components/TypedText'
import SlideUpText from '@/components/SlideUpText'
import FadeDropText from '@/components/FadeDropText'
import 'vue-slick-carousel/dist/vue-slick-carousel.css'
import lottie from 'lottie-web'
import { Howl } from 'howler'
import anime from 'animejs/lib/anime.es.js'

import Editorial from '@/components/Editorial'
import InfographicFullscreen from '@/components/InfographicFullscreen'
import SplitContent from '@/components/SplitContent'
import TwoByTwo from '@/components/TwoByTwo'
import TextAndMedia from '@/components/TextAndMedia'

const fb = require('@/firebase')

export default {
  name: 'Story',
  components: {
    VueSlickCarousel,
    SlideUpText,
    FadeDropText,
    TypedText,

    Editorial,
    InfographicFullscreen,
    SplitContent,
    TwoByTwo,
    TextAndMedia
  },
  data () {
    return {
      loading: true,
      supportsPassive: false,

      aspect: '',
      mobileLowerThirds: false,
      mobileMode: false,
      mobileWidth: 480,
      screenHeight: '',
      hideFullscreen: false,
      showTransitionStill: false,

      useFade: true,

      quoteBgColor: '#f5f0ec',
      gotQuoteBgColor: false,
      bgStillImage: '',
      // Slide data
      story: {},
      mainTheme: '',
      storyLoaded: false,

      // Has the user started the story yet?
      storyStarted: false,
      // Is the story currently playing?
      storyPlaying: false,
      // Has the story finished playing?
      storyFinished: false,
      // What is the current slide?
      viewedAudioNotification: false,

      currentSlide: 0,
      currentSlideData: {},
      currentProgressBarSlide: 0,
      currentCopySegment: 0,
      totalCopySegments: 0,
      currentSection: '',
      currentSectionIndex: -1,
      currentMusicSrc: '',
      currentWidget: '',
      showingWidget: false,
      showingPersistentSectionTitle: true,
      // Which slide will be next?
      targetSlide: -1,
      movingToNext: false,
      slideTitleHidden: true,
      currentSlideDuration: 0,
      currentSlideInitialized: false,
      currentSlideHasVideo: false,
      currentSlideHasInfographic: false,

      progressPercentage: 0,
      // 0 is the first jump point, so -1 means none is active
      currentJumpPoint: -1,
      totalJumpPoints: 0,
      jumping: false,

      coverHidden: false,

      navShown: false,
      minimalArrows: false,

      clock: 0,
      currentTime: 0,
      counter: null,
      timeSinceTransition: 0,
      waitingForVideo: false,
      showReload: false,
      videoStarted: false,
      showVidCover: false,

      slidesInitialized: false,
      loadedAnimations: false,
      animations: [],
      featureAnimations: [],
      ltAnimations: [],
      ltAnimStarted: false,

      chosenAnswers: [],
      answerResults: [],
      answersSubmitted: [],
      showingResults: [],
      resultsReshown: false,
      multipleAnswers: false,

      syncAudioClips: [],
      bgAudioClips: [],
      bgAudioSrc: [],
      currentBgAudio: '',
      currentSyncAudio: '',

      textAnimationDelay: 2000,
      infographicDelay: 0,
      infographicStarted: false,
      syncAudioStarted: false,
      awaitingNext: false      
    }
  },

  computed: {
    /**
     * Computes the appropriate "Play" image file to use based on the current story.id
     */
    playFile () {
      switch (this.story.id) {
        case 'studio-shows':
          return 'start_orange.svg'
        case 'nfl-female-fans':
          return 'start_red.svg'
        case 'mlb':
          return 'start_white.svg'
        case 'world-cup':
          return 'start_white-transp.svg'
        default:
          return 'start.svg'
      }
    },
    /**
     * Computes the appropriate "Replay" image file to use based on the current story.id
     */
    replayFile () {
      switch (this.story.id) {
        case 'studio-shows':
          return 'replay_orange.svg'
        case 'nfl-female-fans':
          return 'replay_red.svg'
        case 'betting':
          return 'replay_drawn.png'
        case 'mlb':
          return 'replay_white.svg'
        case 'world-cup':
          return 'replay_white-transp.svg'
        default:
          return 'replay.svg'
      }
    },
    // Used to combine sections and beats into a single array for carousel.
    storyBeats () {
      let slides = []
      if (this.story.thesis) {
        const thesis = this.story.thesis
        thesis.type = 'thesis'
        thesis.sectionIndex = 0
        slides.push(thesis)
      }
      // If the Story has beats not associated with a particular section
      if (this.story.beats) {
        this.story.beats.forEach((beat) => {
          if (!beat.duration) beat.duration = 4000
          beat.section = ''
          beat.sectionIndex = 0
          slides.push(beat)
        })
      }
      if (this.story.sections) {
        this.story.sections.forEach((section, index) => {
          section.type = 'section-title'
          section.sectionIndex = index + 1
          // Default section duration
          if (!section.duration) section.duration = 3000
          if (!section.noSlide) slides.push(section)
          if (section.beats) {
            section.beats.forEach((beat) => {
              // Default beat duration
              if (!beat.duration) beat.duration = 4000
              beat.section = section.title
              if (beat.customGroup) {
                beat.sectionIndex = this.story.sections.length + beat.customGroup + 1
              } else {
                beat.sectionIndex = index + 1
              }
              
              slides.push(beat)
            })
          }
        })
      }

      return slides
    },

    // Calculate only slides which should be used as part of the outline
    // (Currently not in use)
    outlineSections () {
      // Make a copy of the story without modifying the original
      let outlineStory = JSON.parse(JSON.stringify(this.story))
      if (outlineStory.sections) {
        outlineStory.sections.forEach((section) => {
          if (section.beats) {
            for (let i = section.beats.length - 1; i > 0; i--) {
              if (section.beats[i].inOutline === false) {
                section.beats.splice(i, 1)
              }
            }
          }
        })
      }
      return outlineStory.sections
    },

    progressBarTitles () {
      const titles = []
      if (this.story && this.story.sections) {
        this.story.sections.forEach((section) => {
          if (section.inProgressBar !== false) {
            titles.push(section.title)
          }
        })
      }
      return titles
    },

    titleWidths () {
      let totalTitles = 0
      const widths = []
      this.story.sections.forEach((section) => {
        if (section.inProgressBar !== false) {
          totalTitles += section.titleWidthRatio || 1
        }
      })
      const titleWidth = 100 / totalTitles
      this.story.sections.forEach((section) => {
        if (section.inProgressBar !== false) {
          if (section.titleWidthRatio) {
            widths.push(section.titleWidthRatio * titleWidth)
          } else {
            widths.push(titleWidth)
          }
        }
      })
      return widths
    },

    progressBarWidths () {
      let totalSegments = 0
      const widths = []
      this.progressBarSegments.forEach((beat) => {
        totalSegments += beat.widthRatio || 1
      })
      const segmentWidth = 100 / totalSegments
      this.progressBarSegments.forEach((beat) => {
        if (beat.widthRatio) {
          widths.push(beat.widthRatio * segmentWidth)
        } else {
          widths.push(segmentWidth)
        }
      })
      return widths
    },

    progressBarSegments () {
      const segments = []
      this.storyBeats.forEach((beat, i) => {
        beat.rootIndex = i
        segments.push(beat)
        // If the beat is a video and has jump points (target times to jump to / from)
        if (beat.type === 'video' && beat.jumpPoints) {
          // Loop through each jump point and add a segment for each.
          beat.jumpPoints.forEach((point, j) => {
            const jump = {}
            jump.rootIndex = i
            jump.jumpIndex = j
            jump.jumpPoint = point
            segments.push(jump)
          })
        }
      })
      return segments
    },

    progressBarColors () {
      const colors = []
      this.storyBeats.forEach((beat) => {
        if (beat.loaderColor) {
          colors.push(beat.loaderColor)
        } else {
          colors.push(undefined)
        }
      })
      return colors
    },

    loaderColor () {
      let outputColor = 'white'
      switch(this.story.id) {
        case 'studio-shows':
          outputColor = 'accentSS'
          break
        case 'college-football':
          outputColor = 'accent'
          break
        default:
          outputColor = 'white'
      }
      return outputColor
    }
  },

  mounted () {
    this.checkPassive()
    // Check for the "viewed audio notification" cookie
    // this.viewedAudioNotification = this.getCookie('viewedAudioNotification')

    const classes = (this.$router.currentRoute.path.replaceAll('/', ' ').trim()).split(' ')
    const targetStory = classes[classes.length-1]
    // const targetStory = this.$router.currentRoute.path.replace('/', '')
    console.log(`Target story: ${targetStory}`)
    
    let storyQuery = fb.storiesCollection.doc(targetStory)

    storyQuery.get().then((story) => {
      this.story = story.data()
      this.mainTheme = this.story.theme || ''
      console.log('Got the story.')
      this.storyLoaded = true
      this.waitForCover()
    })

    const app = this

    function checkAspectRatio () {
      if (window.innerWidth > window.innerHeight) {
        app.aspect = 'landscape'
      } else {
        app.aspect = 'portrait'
      }
      if (window.innerWidth < 1280) {
        app.mobileLowerThirds = true
      } else {
        app.mobileLowerThirds = false
      }
      if (window.innerWidth < app.mobileWidth) {
        app.mobileMode = true
      } else {
        app.mobileMode = false
      }
    }

    window.onresize = () => {
      checkAspectRatio()
      // this.setFullscreenVidPos()
      this.setSlidesHeight()
      this.setNavHeight()
    }

    checkAspectRatio()
  },

  methods: {
    logout () {
      this.$root.$emit('logout')
    },

    // Used to check for a particular cookie value
    getCookie (cname) {
      const name = cname + '='
      const decodedCookie = decodeURIComponent(document.cookie)
      const cookieArray = decodedCookie.split('; ')
      for (let i = 0; i < cookieArray.length; i++) {
        let c = cookieArray[i]
        while(c.charAt(0) == ' ') {
          c = c.substring(1)
        }
        if (c.indexOf(name) == 0) {
          return c.substring(name.length, c.length)
        }
      }
      return ''
    },

    waitForCover () {
      if (this.story.cover) {
        const img = new Image()
        img.onload = () => {
          console.log('Loaded cover image.')
          this.loading = false
        }
        img.src = this.story.cover.background
        if (img.complete) img.onload()
      } else {
        console.log('This story has no cover images.')
        this.loading = false
      }
      setTimeout(() => {
        console.log('Cover timed out check.')
        if (this.loading) this.loading = false
      }, 4000)
    },

    // Toggle the navigation overlay
    toggleNav (state) {
      this.navShown = state
      
      if (this.navShown) {
        document.getElementsByTagName('body')[0].classList.add('nav-shown')
        this.pauseStory()
      } else {
        document.getElementsByTagName('body')[0].classList.remove('nav-shown')
      }
    },

    // Subroutine for disabling mobile scroll.
    checkPassive () {
      try {
        window.addEventListener('test', null, Object.defineProperty({}, 'passive', {
          get: () => {
            this.supportsPassive = true
            return true
          }
        }))
      } catch(e) {
        console.log(e)
      }
    },

    disableEvent (e) {
      e.preventDefault()
    },

    disableMobileScroll () {
      document.getElementsByTagName('body')[0].addEventListener('touchmove', this.disableEvent, this.supportsPassive ? { passive: false } : false)
    },
    enableMobileScroll () {
      document.getElementsByTagName('body')[0].removeEventListener('touchmove', this.disableEvent, this.supportsPassive ? { passive: false } : false)
    },

    /**
     * Helper Subroutines
     */
    initialLoadCheck () {
      if (!this.loadedAnimations) {
        this.initAnimations()
        this.initAudio()
        this.loadedAnimations = true
      }
    },
    setSlidesHeight () {
      // console.log('Setting slide heights.')
      const slides = document.querySelectorAll('.slick-slide > div')
      slides.forEach((slide) => {
        slide.style.height = `${window.innerHeight}px`
      })
    },
    setFullscreenVidPos () {
      // console.log('Setting video positions.')
      this.storyBeats.forEach((beat, index) => {
        if (beat.backgroundVideo) {
          const currentVid = this.$refs[`video-${index}`]
          if (currentVid && currentVid.classList.contains('fullscreen')) {
            currentVid.style.top = `${(window.innerHeight / 2) - (currentVid.offsetHeight / 2)}px`
            currentVid.style.left = `${(window.innerWidth / 2) - (currentVid.offsetWidth / 2)}px`
          }
        }
      })
    },
    
    setNavHeight () {
      const navigation = document.querySelector('.story-nav')
      if (navigation) {
        navigation.style.height = `${window.innerHeight - 145}px`
        if (window.innerWidth < 768) {
          navigation.style.height = `${window.innerHeight - 105}px`
        }
        if (window.innerWidth < 540 || window.innerHeight < 540) {
          navigation.style.height = `${window.innerHeight}px`
        }
      }
    },

    // Used to calculate which slide to go to from story navigation
    calcSlideIndex (sIndex, bIndex) {
      let outputIndex = sIndex
      let removedSlideOffset = 0
      for (let i = 0; i < sIndex; i++) {
        outputIndex += this.story.sections[i].beats.length
        // Account for any removed slides in the offset
        if (this.story.sections[i].noSlide) removedSlideOffset++
      }
      if (bIndex !== undefined) {
        outputIndex += bIndex
        // Add one for section cover if it has its own slide
        !this.story.sections[sIndex].noSlide ? outputIndex += 1 : outputIndex += 0
      }
      
      // Include an offset for the Thesis or other beats not included in sections
      const offset = (this.story.thesis ? 1 : 0) + (this.story.beats ? this.story.beats.length : 0)
      // console.log(`Section: ${sIndex} Beat: ${bIndex} Output index: ${outputIndex} Removed: ${removedSlideOffset}`)
      return outputIndex + offset - removedSlideOffset
    },

    // Used to determine the widths of various story progress bars.
    loaderPercent (i) {
      if (i == this.currentProgressBarSlide) {
        return this.progressPercentage
      } else if (i < this.currentProgressBarSlide) {
        return 100
      } else if (i > this.currentProgressBarSlide) {
        return 0
      }
    },

    getSectionTitle (beatData) {
      if (this.aspect === 'portrait' && beatData.titleMobile) {
        return beatData.titleMobile
      } else if (beatData.titleStyled) {
        return beatData.titleStyled
      } else {
        return beatData.title
      }
    },

    getBgImage (input) {
      if (input.backgroundMobile && this.aspect === 'portrait') {
        return input.backgroundMobile
      } else {
        return input.background
      }
    },

    // For fullscreen videos, get the appropriate video based on screen size.
    getBgVideo (input) {
      // If the background image is just a string
      if (typeof(input) === 'string') {
        return input
      } else if (typeof(input) === 'object') {
        if (this.aspect === 'landscape') {
          return input.landscape || ''
        } else if (this.aspect === 'portrait') {
          // If mobile mode, use the mobile version of the video
          if (this.mobileMode && input.mobile) {
            return input.mobile
          } else {
            return input.portrait || ''
          }
        }
      }
    },

    getVideoClass (input) {
      if (typeof(input) === 'string') {
        return 'default'
      } else if (typeof(input) === 'object') {
        if (this.aspect === 'landscape') {
          return 'landscape'
        } else if (this.aspect === 'portrait') {
          if (this.mobileMode && input.mobile) {
            return 'mobile'
          } else {
            return 'portrait'
          }
        }
      }
    },

    getImage (input) {
      if (typeof(input) === 'string') {
        return input
      } else if (typeof(input) === 'object') {
        if (this.aspect === 'landscape') {
          return input.desktop || ''
        } else if (this.aspect === 'portrait') {
          return input.mobile || ''
        }
      }
    },

    getSlideBg (input) {
      if (this.aspect === 'landscape') {
        return (input.cover && input.cover.back) ? `url(${input.cover.back})` : ''
      } else if (this.aspect === 'portrait') {
        return (input.coverMobile && input.coverMobile.back) ? `url(${input.coverMobile.back})` : ''
      }
    },

    getTargetInfographic(input) {
      let targetAnimation = input
      if (typeof input === 'object' && input !== null) {
        if (this.aspect == 'landscape') {
          targetAnimation = input.desktop
        } else {
          targetAnimation = input.mobile
        }
      }
      return targetAnimation
    },

    getCopy(input) {
      if (Array.isArray(input)) {
        return input
      } else {
        return [input]
      }
    },

    // Check to see if there's a lower third. If there is, play it.
    lowerThirdPlayCheck () {
      const currentLowerThird = this.$refs[`lower-third-${this.currentSlide}`]
      if (currentLowerThird && this.ltAnimStarted) {
        this.ltAnimations[this.currentSlide].play()
      }
    },

    // Used to initialize a slide, usually after a change
    slideInit (slideIndex) {
      // console.log(`Initializing slide ${slideIndex}`)
      // Resets
      if (!this.viewedAudioNotification) {
        this.dismissAudioNotification()
      }
      this.storyFinished = false
      
      this.movingToNext = false
      this.timeSinceTransition = 0
      this.waitingForVideo = false
      this.showReload = false
      this.videoStarted = false
      this.resultsReshown = false

      // Infographics
      this.awaitingNext = false
      this.infographicStarted = false
      this.syncAudioStarted = false
      this.currentSlideHasInfographic = false

      this.clock = 0

      this.currentSlideHasVideo = false
      this.hideFullscreen = false

      this.currentSlide = slideIndex
      this.currentSlideData = this.storyBeats[this.currentSlide]
      this.calculateProgressBarIndex()
      this.currentSlideDuration = this.currentSlideData.duration
      const ignoreVidDuration = this.currentSlideData.ignoreVideoDuration
      this.currentSection = ''
      this.currentCopySegment = 0
      this.totalCopySegments = 0
      this.currentJumpPoint = -1
      this.totalJumpPoints = this.currentSlideData.jumpPoints ? this.currentSlideData.jumpPoints.length : 0
      this.ltAnimStarted = false
      this.currentSyncAudio = ''

      const currentType = this.currentSlideData.type
      
      const featureAnimation = this.$refs[`feature-animation-${this.currentSlide}`]

      this.currentSyncAudio = this.syncAudioClips[this.currentSlide]

      const tempTheme = this.currentSlideData.tempTheme || this.mainTheme
      this.story.theme = tempTheme

      if (featureAnimation) {
        const targetAnimation = this.animations[this.currentSlide]
        // Currently, "nextDelay" only applies to full infographic slides.
        this.currentSlideDuration = targetAnimation.getDuration(false) * 1000 + (this.currentSlideData.nextDelay || 0)
        if (this.storyPlaying) {
          targetAnimation.play()

          if (this.currentSyncAudio) {
            this.currentSyncAudio.play()
            this.syncAudioStarted = true
          }
        }
      }

      if (
        this.currentSectionIndex !== this.currentSlideData.sectionIndex &&
        this.bgAudioSrc[this.currentSectionIndex] !== this.bgAudioSrc[this.currentSlideData.sectionIndex]
      ) {
        const currentAudio = this.bgAudioClips[this.currentSlideData.sectionIndex]
        if (currentAudio) {
          this.currentBgAudio = currentAudio
          if (this.storyPlaying && (currentType !== 'video' || this.currentSlideData.customGroup || this.currentSlideData.noAudio)) {
            this.currentBgAudio.volume(this.story.defaultVolume || 1)
            this.currentBgAudio.play()
          }
        }
      } else if (this.storyPlaying && this.currentBgAudio && !this.currentBgAudio.playing()) {
        this.currentBgAudio.play()
      }

      if (this.currentBgAudio) {
        const defaultVolume = this.story.defaultVolume || 1
        const targetVolume = this.currentSlideData.audioVolume || defaultVolume
        if (currentType === 'video' && !this.currentSlideData.noAudio) {
          if (this.currentSlideData.customGroup) {
            console.log('This video is part of a custom group. Fading in.')
            this.currentBgAudio.fade(this.currentBgAudio.volume(), 0.7, 400)
          } else {
            this.currentBgAudio.fade(this.currentBgAudio.volume(), 0, 400)
          }
        } else if (
          currentType === 'testimonial' ||
          currentType === 'quote'
        ) {
          if (this.currentBgAudio.volume() !== 0.2) {
            this.currentBgAudio.fade(this.currentBgAudio.volume(), 0.2, 400)
          }
        } else if (targetVolume && this.currentBgAudio.volume() !== targetVolume) {
          console.log('Setting target volume to', targetVolume)
          this.currentBgAudio.fade(this.currentBgAudio.volume(), targetVolume, 400)
        } else if (this.currentBgAudio.volume() < 1) {
          this.currentBgAudio.fade(this.currentBgAudio.volume(), this.story.defaultVolume || 1, 400)
        }
      }

      if (currentType === 'infographic') {
        this.slideTitleHidden = false
        this.$refs.slideTitle.innerHTML = this.currentSlideData.title
      }

      if (currentType === 'question') {
        this.chosenAnswers = new Array(this.currentSlideData.answers.length)

        if (Array.isArray(this.currentSlideData.correct)) {
          console.log('There are multiple correct answers.')
          this.multipleAnswers = true
        } else {
          this.multipleAnswers = false
        }
        this.minimalArrows = true
      } else {
        this.minimalArrows = false
      }

      this.currentSection = this.currentSlideData.section
      this.currentSectionIndex = this.currentSlideData.sectionIndex

      const currentInfographic = this.$refs[`infographic-${this.currentSlide}`]
      if (currentInfographic) {
        this.currentSlideHasInfographic = true
        const currentAnimation = this.animations[this.currentSlide]

        this.infographicDelay = this.currentSlideData.infographicDelay
        this.currentSlideDuration = currentAnimation.getDuration(false) * 1000 + this.infographicDelay
      }

      const currentVid = this.$refs[`video-${this.currentSlide}`]
      if (currentVid) {
        if (!currentInfographic && !featureAnimation && !ignoreVidDuration) {
          this.currentSlideHasVideo = true
          this.currentSlideDuration = currentVid.duration * 1000
        }
        
        if (this.storyPlaying) {
          currentVid.play()
        }

        const lowerThirdDelay = currentType == 'quote' ? this.currentSlideData.lowerThirdDelay : 500

        if (!currentInfographic && !featureAnimation && !ignoreVidDuration) {
          currentVid.ontimeupdate = () => {
            this.clock = currentVid.currentTime * 1000
            if (!this.videoStarted && this.clock > 25) {
              this.videoStarted = true
              this.storyPlaying = true
              this.waitingForVideo = false
              this.showReload = false
            }

            if (this.clock > lowerThirdDelay && !this.ltAnimStarted && this.storyPlaying) {
              this.ltAnimStarted = true
              this.lowerThirdPlayCheck()
            }
          }

          currentVid.onplay = () => {
            this.currentSlideDuration = currentVid.duration * 1000
          }

          currentVid.onloadstart = () => {
            this.currentSlideDuration = currentVid.duration * 1000
          }
        }
      }
      const currentCopy = this.currentSlideData.copy
      if (currentCopy) {
        this.totalCopySegments = (this.getCopy(currentCopy)).length
      }

      const targetSection = this.story.sections[this.currentSlideData.sectionIndex - 1]
      if (targetSection) {
        this.currentWidget = targetSection.widget || ''
      }
      if (this.currentWidget) {
        setTimeout(() => {
          this.showingWidget = true
        }, 300)
      }

      this.currentSlideInitialized = true
    },

    playTextAnimations () {
      anime.timeline({
        loop: 0
      }).add({
        loop: 0,
        targets: `.results-${this.currentSlide} .type-on`,
        opacity: [0,1],
        easing: 'easeInOutExpo',
        duration: 1000,
        delay: (el, i) => i * 200
      })
    },

    initLowerThirds () {
      const app = this
      this.storyBeats.forEach((beat, index) => {
        const currentLowerThird = this.$refs[`lower-third-${index}`]
        if (currentLowerThird) {
          app.ltAnimations[index] = this.loadAnimation(currentLowerThird)
        }
      })
    },

    initAnimations () {
      console.log('Initializing animations.')
      const app = this

      this.storyBeats.forEach((beat, index) => {
        const currentInfographic = this.$refs[`infographic-${index}`]
        if (currentInfographic) {
          app.animations[index] = this.loadAnimation(currentInfographic)
        }
        const currentTextAnimation = this.$refs[`feature-animation-${index}`]
        if (currentTextAnimation) {
          app.animations[index] = this.loadAnimation(currentTextAnimation)
        }
        if (beat.audio) {
          app.syncAudioClips[index] = new Howl({
            src: [ beat.audio ],
            html5: true
          })
        }
      })
    },

    loadAnimation (element) {
      const data = lottie.loadAnimation({
        container: element,
        renderer: 'svg',
        loop: false,
        autoplay: false,
        path: element.getAttribute('data-animation'),
        rendererSettings: {
          progressiveLoad: true
        }
      })

      return data
    },

    initAudio () {
      const app = this

      if (this.story.thesis && this.story.thesis.music) {
        app.bgAudioSrc[0] = this.story.thesis.music
        app.bgAudioClips[0] = new Howl({
          src: [this.story.thesis.music],
          loop: true
        })
      }

      this.story.sections.forEach((section, index) => {
        if (section.music) {
          app.bgAudioSrc[index + 1] = section.music
          app.bgAudioClips[index + 1] = new Howl({
            src: [ section.music ],
            loop: true
          })
        }
      })

      this.storyBeats.forEach((beat) => {
        // This means only one in custom group should have music (or the clip would be overwritten)
        if (beat.customGroup && beat.music) {
          console.log('Adding custom group music.')
          app.bgAudioSrc[app.story.sections.length + beat.customGroup + 1] = beat.music
          app.bgAudioClips[app.story.sections.length + beat.customGroup + 1] = new Howl({
            src: [beat.music],
            loop: true
          })
        }
      })
    },

    slideWithinRange (index) {
      if (Math.abs(this.targetSlide - index) > 2) {
        return false
      } else {
        return true
      }
    },

    checkSlidesForLoad (targetIndex) {
      this.storyBeats.forEach((beat, index) => {
        if (Math.abs(targetIndex - index) <= 2) {
          beat.loaded = true
        }
      })
    },

    calculateProgressBarIndex () {
      // Default to slide index
      this.currentProgressBarSlide = this.currentSlide
      // Then add offset if necessary
      this.progressBarSegments.forEach((segment) => {
        if (segment.jumpPoints && this.currentSlide > segment.rootIndex) {
          this.currentProgressBarSlide += (segment.jumpPoints.length)
        }
      })
      if (this.currentJumpPoint > -1) {
        this.currentProgressBarSlide += (this.currentJumpPoint + 1)
      }
    },

    nextSlideCheck () {
      if (this.currentJumpPoint < this.totalJumpPoints - 1) {
        return
      }
      this.$refs.storyCarousel.next()
    },

    /**
     * Story Controls
     */
    clickStartButton () {
      if (this.viewedAudioNotification) {
        this.startStory()
      } else {
        this.hideCover()
      }
    },

    dismissAudioNotification () {
      this.viewedAudioNotification = true
      // Mark the "viewedAudioNotification" cookie as true, so it's hidden on next visit.
      const date = new Date()
      // 30 days
      date.setTime(date.getTime() + (30 * 24 * 60 * 60 * 1000))
      const expires = `; expires=${date.toGMTString()}`
      document.cookie = `viewedAudioNotification=true; ${expires}; path=/`
    },
    
    hideCover () {
      this.coverHidden = true
    },

    startStory () {
      this.storyPlaying = true
      if (this.story.music && !this.currentBgAudio) {
        this.currentBgAudio = this.bgAudioClips[0]
      }
      this.currentWidget = this.story.sections[0].widget || ''

      this.slideInit(0)
      this.checkSlidesForLoad(0)
      this.coverHidden = true
      this.storyStarted = true
      this.targetSlide = 0

      this.startClock()
      this.setNavHeight()
    },

    resetStory () {
      if (!this.storyStarted) {
        return
      }
      this.goToSlide(0)
      this.pauseStory()
      this.answersSubmitted = []
      this.showingResults = []
      this.coverHidden = false
      this.storyStarted = false
      this.storyPlaying = false
      this.storyFinished = false
      this.afterChange(0)
      this.setNavHeight()
      if (this.currentBgAudio) {
        this.currentBgAudio.stop()
      }
    },

    pauseStory () {
      this.storyPlaying = false

      const currentVid = this.$refs[`video-${this.currentSlide}`]
      if (currentVid) currentVid.pause()
      const currentAnimation = this.animations[this.currentSlide]
      if (currentAnimation) currentAnimation.pause()

      if (this.currentSyncAudio) this.currentSyncAudio.pause()
      const currentLowerThird = this.$refs[`lower-third-${this.currentSlide}`]
      if (currentLowerThird) this.ltAnimations[this.currentSlide].pause()

      if (this.currentBgAudio) this.currentBgAudio.pause()
    },

    resumeStory () {
      this.toggleNav(false)
      this.storyPlaying = true
      if (!this.viewedAudioNotification) {
        this.dismissAudioNotification()
      }

      const currentVid = this.$refs[`video-${this.currentSlide}`]
      if (currentVid) currentVid.play()

      const currentAnimation = this.animations[this.currentSlide]
      if (currentAnimation && this.infographicStarted) {
        this.startInfographic()
        if (this.currentSyncAudio) {
          this.currentSyncAudio.play()
        }
      }

      const featureAnimation = this.$refs[`feature-animation-${this.currentSlide}`]
      if (featureAnimation) {
        this.animations[this.currentSlide].play()
        if (this.currentSyncAudio) {
          this.currentSyncAudio.play()
        }
      }

      this.lowerThirdPlayCheck()

      const currentType = this.storyBeats[this.currentSlide].type
      if (this.currentBgAudio && !this.currentBgAudio.playing() && (currentType !== 'video' || this.currentSlideData.customGroup || this.currentSlideData.noAudio)) {
        this.currentBgAudio.play()
      }
    },

    startInfographic () {
      console.log('Start infographic.')
      const currentAnimation = this.animations[this.currentSlide]
      if (currentAnimation) currentAnimation.play()
      if (!this.infographicStarted) {
        this.currentSyncAudio = this.syncAudioClips[this.currentSlide]
        if (this.currentSyncAudio && !this.syncAudioStarted) this.currentSyncAudio.play()
      }
      
      this.infographicStarted = true
      this.syncAudioStarted = true
      this.awaitingNext = false
    },

    chooseAnswer (chosenIndex) {
      const choices = Array.from(this.chosenAnswers)
      if (this.multipleAnswers) {
        choices[chosenIndex] ? choices[chosenIndex] = false : choices[chosenIndex] = true
      } else {
        for (let i = 0; i < choices.length; i++) {
          choices[i] = false
        }
        choices[chosenIndex] = true
      }      
      
      this.chosenAnswers = Array.from(choices)
    },

    submitAnswers () {
      console.log('Submitting answers')
      this.answersSubmitted[this.currentSlide] = true
      this.resultsReshown = true
      this.clock = this.storyBeats[this.currentSlide].duration * 0.66

      setTimeout(() => {
        this.showingResults[this.currentSlide] = true
        this.playTextAnimations()
      }, 1500)
    },

    /**
     * Clock Functions
     */
    startClock () {
      this.currentTime = Date.now()
      this.counter = setInterval(this.clockUpdate)
    },

    stopClock () {
      if (this.counter) {
        clearInterval(this.counter)
        this.counter = null
      }
    },

    clockUpdate () {
      const now = Date.now()
      // If a video is playing, that's used to set the clock time instead.
      if (this.storyPlaying) {
        this.timeSinceTransition += now - this.currentTime
        
        // If the slide has video and it hasn't started, and the time is greater than 750ms...
        if (this.currentSlideHasVideo && !this.videoStarted && this.timeSinceTransition > 750) {
          this.waitingForVideo = true
        }

        if (this.currentSlideHasVideo && !this.videoStarted && this.timeSinceTransition > 3000) {
          this.showReload = true
          this.storyPlaying = false
        }

        if (!this.currentSlideHasVideo && !this.awaitingNext) {
          this.clock += now - this.currentTime
        }
        
        // For infographic slides
        if (this.currentSlideHasInfographic && this.clock >= this.infographicDelay && !this.infographicStarted) {
          this.startInfographic()
        }
        
        // Jump points
        const jumpPoints = this.currentSlideData.jumpPoints

        let segmentDuration = this.currentSlideDuration
        let clockFormatted = this.clock
        
        const currentVid = this.$refs[`video-${this.currentSlide}`]
        
        if (jumpPoints) {
          let endPoint = currentVid.duration * 1000
          let inSegment = false
          jumpPoints.forEach((point, i) => {
            if (this.clock > point) {
              this.currentJumpPoint = i
              inSegment = true
            }
          })
          if (!inSegment) this.currentJumpPoint = -1

          if (this.currentJumpPoint < jumpPoints.length - 1) {
            endPoint = jumpPoints[this.currentJumpPoint + 1]
          }
          if (this.currentJumpPoint == -1) {
            segmentDuration = jumpPoints[0]
          } else {
            segmentDuration = endPoint - jumpPoints[this.currentJumpPoint]
            clockFormatted = this.clock - jumpPoints[this.currentJumpPoint]
          }
        }
        // console.log(`Segment duration: ${segmentDuration}`)
        // this.calculateProgressBarIndex()
        
        this.progressPercentage = Math.min((clockFormatted / segmentDuration) * 100, 100)
        if (this.totalCopySegments > 0) {
          let copyPercent = 0
          if (this.infographicDelay) {
            copyPercent = Math.min((this.clock / this.infographicDelay) * 100)
          } else {
            copyPercent = Math.min((this.clock / this.currentSlideDuration) * 100)
          }
          
          let copyIndex = Math.min(Math.floor(copyPercent / (100 / this.totalCopySegments)), this.totalCopySegments)
          this.currentCopySegment = copyIndex
        }

        if (this.storyBeats[this.currentSlide] !== 'undefined' && this.storyBeats[this.currentSlide].type === 'question') {
          if (
            this.progressPercentage > 22 &&
            this.progressPercentage < 66 &&
            this.answersSubmitted[this.currentSlide] === true &&
            !this.resultsReshown
          ) {
            console.log('This question has been answered. Skipping to results early.')
            this.clock = this.storyBeats[this.currentSlide].duration * 0.66
          }
          if (this.progressPercentage >= 66 && (this.answersSubmitted[this.currentSlide] !== true || !this.resultsReshown)) {
            console.log('Automatically submitting answer.')
            this.resultsReshown = true
            this.chosenAnswers = this.storyBeats[this.currentSlide].correct
            this.submitAnswers()
          }
        }

        if (
          !this.movingToNext && 
          this.progressPercentage >= 100 &&
          this.storyPlaying &&
          this.currentSlide < this.storyBeats.length - 1
        ) {
          this.nextSlideCheck()
        }

        if (
          !this.storyFinished &&
          this.progressPercentage >= 100 &&
          this.currentSlide >= this.storyBeats.length - 1 &&
          this.currentJumpPoint >= this.totalJumpPoints - 1
        ) {
          this.storyFinished = true
          console.log('The story has finished playing.')
        }
      }
      this.currentTime = now
    },

    /**
     * Callback Functions
     */
    carouselReady () {
      console.log('Carousel setup complete.')
    },

    carouselReInit () {
      if (!this.slidesInitialized) {
        this.initLowerThirds()
        this.setSlidesHeight()
        // this.setFullscreenVidPos()
        this.setNavHeight()
        this.initialLoadCheck()
        this.slidesInitialized = true
      }
    },

    beforeChange (slideIndex, nextSlideIndex) {
      this.checkSlidesForLoad(nextSlideIndex)
      // this.setFullscreenVidPos()
      if (slideIndex < nextSlideIndex) {
        // console.log('Moving forward.')
      }
      if (slideIndex > nextSlideIndex) {
        // console.log('Moving back.')
      }
      this.targetSlide = nextSlideIndex
      if (this.storyBeats[this.targetSlide].type !== 'infographic') {
        this.slideTitleHidden = true
      }
      // Hide the section title before slide change.
      if (this.storyBeats[this.targetSlide].type === 'section-title') {
        this.currentSection = ''
      }

      // If targetSlide is in a new section, fade out current music.
      if (
        this.storyBeats[this.targetSlide].sectionIndex !== this.storyBeats[slideIndex].sectionIndex &&
        this.bgAudioSrc[this.storyBeats[slideIndex].sectionIndex] !== this.bgAudioSrc[this.storyBeats[this.targetSlide].sectionIndex]
      ) {
        // console.log(`Moving to a new section: ${this.storyBeats[this.targetSlide].sectionIndex}`)
        const targetSectionIndex = this.storyBeats[this.targetSlide].sectionIndex
        if (this.bgAudioClips[targetSectionIndex]) {
          if (this.currentBgAudio) {
            this.currentBgAudio.fade(this.currentBgAudio.volume(), 0, 400)
            this.currentBgAudio.once('fade', () => {
              this.currentBgAudio.stop()
            })
          }
        }
      } 

      const nextTheme = this.storyBeats[this.targetSlide].theme || ''
      if (nextTheme === 'dark') {
        document.querySelector('.v-application').classList.add('dark')
      } else {
        document.querySelector('.v-application').classList.remove('dark')
      }

      const nextBgStill = this.getBgVideo(this.storyBeats[this.targetSlide].backgroundStill)
      if (nextBgStill) {
        this.showTransitionStill = true
        if (nextBgStill !== this.bgStillImage) {
          document.querySelector('.story-container').style.backgroundImage = `url(${nextBgStill})`
        }
        this.bgStillImage = nextBgStill
      } else if (!nextBgStill) {
        this.showTransitionStill = false
        document.querySelector('.story-container').setAttribute('style', '')
        this.bgStillImage = ''
      }

      if (this.storyBeats[this.targetSlide].sectionIndex !== this.storyBeats[slideIndex].sectionIndex) {
        this.showingWidget = false
        setTimeout(() => {
          this.currentWidget = ''
        }, 300)
      }

      this.waitingForVideo = false
      this.currentSlideInitialized = false
    },

    // Called after change to another slide is complete
    afterChange (slideIndex) {
      // Fill or empty progress bar as necessary
      this.storyPlaying ? this.progressPercentage = 0 : this.progressPercentage = 100

      const prevVid = this.$refs[`video-${this.currentSlide}`]
      const prevAnim = this.animations[this.currentSlide]
      const prevLtAnim = this.ltAnimations[this.currentSlide]
      const prevSyncAudio = this.syncAudioClips[this.currentSlide]
      let prevResults = this.showingResults[this.currentSlide]

      // If slides are changed before a video is finished playing, reset the video
      if (prevVid) {
        prevVid.pause()
        prevVid.currentTime = 0
      }
      if (prevAnim) prevAnim.goToAndStop(0)
      if (prevLtAnim) prevLtAnim.goToAndStop(0)
      if (prevSyncAudio) prevSyncAudio.stop()
      if (prevResults) {
        this.showingResults[this.currentSlide] = false
      }
      this.showTransitionStill = false

      this.chosenAnswers = []

      this.slideInit(slideIndex)
    },

    calcCopyPercent () {
      let targetTotal = this.infographicDelay || this.currentSlideDuration
      this.clock = targetTotal * (this.currentCopySegment / (this.totalCopySegments))
    },

    goToSlide (index) {
      console.log(`Go to slide ${index}.`)
      this.toggleNav(false)
      // Start the story (does not include starting playback)
      if (!this.storyStarted) this.startStory()
      this.$refs.storyCarousel.goTo(index)
    },

    jumpToPoint (index, jumpIndex) {
      this.jumping = true
      console.log(`Jump to segment ${jumpIndex} on slide ${index}`)
      if (index !== this.currentSlide) {
        this.goToSlide(index)
      }
      this.currentJumpPoint = jumpIndex

      const currentVid = this.$refs[`video-${this.currentSlide}`]
      if (currentVid) {
        if (jumpIndex == -1) {
          console.log('Set video to 0')
          currentVid.currentTime = 0
        } else {
          const time = this.currentSlideData.jumpPoints[jumpIndex]
          console.log(`Set time to ${time / 1000.0}`)
          currentVid.currentTime = time / 1000.0
        }
      }
      
      // this.calculateProgressBarIndex()
      this.jumping = false
    },

    nextSlide () {
      if (!this.storyStarted) {
        this.startStory()
        return
      }

      if (this.currentJumpPoint < this.totalJumpPoints - 1) {
        console.log('Jumping to next point.')
        this.currentJumpPoint++
        this.jumpToPoint(this.currentSlide, this.currentJumpPoint)
        return
      }
        
      if (
        (this.storyBeats[this.currentSlide].type !== 'infographic-full' && this.mobileMode && this.currentCopySegment < this.totalCopySegments - 1) ||
        (this.storyBeats[this.currentSlide].type === 'text' && this.currentCopySegment < this.totalCopySegments - 1 && this.totalCopySegments !== 1)
      ) {
        this.currentCopySegment++
        this.calcCopyPercent()
        return
      }
      if (this.awaitingNext && this.storyPlaying) {
        this.startInfographic()
        return
      }
      const currentInfographic = this.$refs[`infographic-${this.currentSlide}`]

      if (currentInfographic && this.clock < this.infographicDelay) {
        this.clock = this.infographicDelay
        if (this.storyPlaying) {
          this.startInfographic()
        } else {
          this.nextSlideCheck()
        }
      } else {
        this.nextSlideCheck()
      }
    },

    prevSlide () {
      // If the copy is divided into multiple segments (on mobile), jump back to the previous segment
      if (
        (this.storyBeats[this.currentSlide].type !== 'infographic-full' && this.mobileMode && this.currentCopySegment > 0 && !this.infographicStarted) ||
        (this.storyBeats[this.currentSlide].type === 'text' && this.currentCopySegment > 0)
      ) {
        this.currentCopySegment--
        this.calcCopyPercent()
        return
      }

      if (this.currentJumpPoint >= 0) {
        console.log('Jumping back.')
        this.currentJumpPoint--
        this.jumpToPoint(this.currentSlide, this.currentJumpPoint)
        return
      }

      if (this.currentSlide == 0) {
        const currentVid = this.$refs[`video-${this.currentSlide}`]
        if (currentVid && currentVid.currentTime > 1) {
          currentVid.currentTime = 0
        }
      }
      
      this.$refs.storyCarousel.prev()
    }
  },
  watch: {
    currentJumpPoint: function(val) {
      console.log(`Current jump point is now ${val}`)
      this.calculateProgressBarIndex()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/style/_variables';
@import '@/assets/style/_fonts';
@import '@/assets/style/_animations';

body.college-football {
  h1 {
    text-transform: uppercase;
  }
}

.fullscreen-overlay {
  @include fill-screen;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;  
  transition: .5s;
}

.story-wrapper {
  background-size: cover;
  background-position: center center;
}

.story-container {
  background-size: cover;
  -webkit-background-size: cover;
  background-position: center center;
}

/***
 *     _        _______          
 *    ( (    /|(  ___  )|\     /|
 *    |  \  ( || (   ) || )   ( |
 *    |   \ | || (___) || |   | |
 *    | (\ \) ||  ___  |( (   ) )
 *    | | \   || (   ) | \ \_/ / 
 *    | )  \  || )   ( |  \   /  
 *    |/    )_)|/     \|   \_/   
 *                               
 */
.story-nav {
  transform: translateY(-110%);
  z-index: $z-navigation;
  box-shadow: $shadow-standard;
  overflow-y: auto;
  transition: .5s all;
  background-color: white;

  h1 {
    font-size: $font-size-ml;
    letter-spacing: .03em;
    
    @include media($bp-tablet) {
      font-size: 36px;
    }
  }

  .subtitle-container {
    margin-bottom: $space-s;
  }

  .nav-subtitle {
    &:nth-child(1) {
      margin-bottom: -4px;
    }
    h2 {
      display: inline-block;
      font-family: $font-body;
      text-transform: uppercase;
      font-size: $font-size-xs;
      font-weight: normal;

      @include media($bp-tablet) {
        font-size: 15px;
      }
      &.first {
        margin-bottom: -2px;
      }
    }
  }

  &.shown {
    transform: translateY(0);
    transition: .5s all;
  }

  .text-container {
    position: relative;
    margin: $space-ml $space-xxl;

    @include media($bp-tablet) {
      margin: #{25px + $space-s * 2} $space-s $space-s;
    }

    .story-reset-container {
      cursor: pointer;
    }

    .left {
      // Font space fix
      transform: translateY(-16px);
      @include media($bp-phone-l) {
        // transform: none;
      }
    }

    .story-outline {
      h2 {
        font-family: $font-display;
        font-size: $font-size-m;
        letter-spacing: .01em;
        text-transform: uppercase;

        @include media($bp-desktop-s) {
          font-size: $font-size-ms;
        }

        &.hidden {
          display: none;
        }
      }
      li {
        font-family: $font-title;
        font-size: 22px;
        line-height: 30px;

        &.hidden {
          display: none;
        }

        &.indent {
          padding-left: $space-s * 2;
        }

        @include media($bp-desktop-s) {
          font-size: $font-size-s;
          line-height: 26px;
        }
      }
      h2,
      li {
        position: relative;
        cursor: pointer;
      }

      ul {
        list-style: none;
        margin-bottom: $space-s;
        padding-left: $space-s;
      }

      .active::before {
        content: '';
        position: absolute;
        bottom: 18%;
        transform: translateX(-16px);
        border-top: 10px solid transparent;
        border-bottom: 10px solid transparent;
        border-left: 10px solid black;

        @include media($bp-desktop-s) {
          border-top-width: 7px;
          border-bottom-width: 7px;
          border-left-width: 7px;
        }
      }
    }

    .logo-container {
      z-index: 1;
      position: absolute;
      top: 0;
      right: 0;
      text-align: center;
      text-transform: uppercase;
      margin-bottom: 130px;

      @include media($bp-phone-l) {
        position: relative;
        margin-top: $space-m;
      }

      .logout {
        margin-bottom: $space-m;
        color: black;

        @include media($bp-phone-l) {
          position: absolute;
          bottom: -90%;
          left: 50%;
          transform: translateX(-50%);
        }
      }

      img {
        height: 64px;
      }

      h2 {
        font-family: $font-display;
        font-size: $font-size-xs;
      }

      h3 {
        font-size: $font-size-xxxs;
        font-family: $font-body;
        font-weight: normal;
      }

      a {
        text-decoration: none;
        color: inherit;
      }
    }
  }
}

// STORY SPECIFIC
body.college-football .story-nav {
  background-color: $brand-light;
  .subtitle-container {
    max-width: 240px;
  }
  .nav-subtitle h2 {
    @include highlight;
  }
}

body.studio-shows .story-nav {
  background-color: $ss-orange;

  h1 {
    text-transform: uppercase;
    font-family: $font-title-heavy;
    font-size: 44px;
    -webkit-text-stroke: 1px white;
    -webkit-font-smoothing: antialiased;
    color: transparent;

    @include media ($bp-desktop-s) {
      max-width: 320px;
      line-height: 1;
      margin-bottom: $space-ms;
    }
  }

  .subtitle-container {
    margin-bottom: $space-ms;
  }

  .nav-subtitle h2 {
    font-family: $font-title-regular;
    font-size: $font-size-ms;
    text-transform: none;
    line-height: 1.1;
    max-width: 450px;

    @include media ($bp-desktop-s) {
      max-width: 360px;
      font-size: $font-size-s;
    }

    @include media($bp-phone-l) {
      
    }
  }

  .story-outline {
    h2 {
      font-family: $font-title-heavy;
      font-size: $font-size-s;

      @include media ($bp-phone-l) {
        font-size: 18px;
      }
    }

    ul {
      margin-bottom: $space-ms;
      padding-left: $space-ms;

      @include media ($bp-desktop-s) {
        padding-left: $space-s;
      }
    }

    li {
      font-family: $font-title-regular;
      font-size: $font-size-s;
      // white-space: nowrap;
      max-width: 100%;
      // overflow: hidden;
      // text-overflow: ellipsis;

      &.indent {
        @include media ($bp-desktop-s) {
          padding-left: $space-s;
        }
      }
    }

    .active::before {
      border-left-color: white;
      border-top-width: 8px;
      border-bottom-width: 8px;
    }
  }

  .logo-container {
    .logout {
      color: $ss-orange;
    }
  }
}

/***
 *     _______  _______           _______  _______ 
 *    (  ____ \(  ___  )|\     /|(  ____ \(  ____ )
 *    | (    \/| (   ) || )   ( || (    \/| (    )|
 *    | |      | |   | || |   | || (__    | (____)|
 *    | |      | |   | |( (   ) )|  __)   |     __)
 *    | |      | |   | | \ \_/ / | (      | (\ (   
 *    | (____/\| (___) |  \   /  | (____/\| ) \ \__
 *    (_______/(_______)   \_/   (_______/|/   \__/
 *                                                 
 */
.story-cover {
  @include bg-cover-center;
  overflow: hidden;
  z-index: $z-story-cover;
  text-align: center;
  transition: .5s all;
  &.hidden {
    left: -100%;
    transition: .5s all;
  }
  .fs-logo {
    height: 96px;
    margin-top: $space-m;

    @include media($bp-phone-l) {
      height: 58px;
    }
  }

  .center {
    @include center-in-parent;

    img {
      @include center-in-parent;
      width: auto;
      max-height: 104.5vh;
    }
  }
}

// STORY SPECIFIC
body.college-football .story-cover {
  background-color: $brand-light;

  .center {
    width: 125%;
    height: 100%;
  }
}

body.studio-shows .story-cover {
  background-color: $ss-purple-dark;

  .center {
    width: 100%;
    h1 {
      color: $ss-orange;
      -webkit-text-stroke: 1px $ss-orange;
      -webkit-font-smoothing: antialiased;
      color: transparent;

      font-family: $font-title-heavy;
      font-size: 150px;
      font-weight: normal;
      line-height: 90%;
      max-width: $bp-desktop-l;
      margin: 0 auto;
      text-transform: uppercase;

      @include media($bp-tablet) {
        font-size: 100px;
      }
      @include media($bp-phone-l) {
        font-size: 80px;
      }
      @include media($bp-phone-m) {
        font-size: 60px;
      }
    }
    h2 {
      margin: $space-m auto;
      max-width: 600px;
      font-size: 38px;
      line-height: 1.1;

      @include media($bp-tablet) {
        font-size: $font-size-ms;
        max-width: 400px;
      }
      @include media($bp-phone-l) {
        margin: 20px auto;
        font-size: $font-size-s;
        max-width: 300px;
      }
    }
  }
}

@media (max-aspect-ratio: 1/1) {
  .story-cover {
    .center {
      img {
        max-height: 100vh;

        @include media($bp-phone-l) {
          max-height: 80vh;
        }
      }
    }
  }
}

.start-button {
  position: absolute;
  right: $space-ml;
  bottom: $space-ml;
  cursor: pointer;

  @include media($bp-phone-l) {
    right: $space-ms;
    bottom: $space-ms;
  }
  
  img {
    pointer-events: none;
    height: 120px;
  }

  p {
    font-family: $font-title;
    text-transform: uppercase;
    margin: 0;
  }

  @include media($bp-desktop-xl) {
    img {
      height: 80px;
    }
  }

  @include media($bp-phone-l) {
    img {
      height: 64px;
    }
    p {
      font-size: 14px;
    }
  }
}

.replay-button {
  cursor: pointer;
  pointer-events: none;
  opacity: 0;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  transition: 0.3s all;

  &.visible {
    pointer-events: all;
    opacity: 1;
    transition: 0.3s all;
  }

  img {
    height: 120px;
    display: block;
  }

  p {
    margin-top: $space-xs;
    font-family: $font-title;
    text-transform: uppercase;
    color: white;
  }
}

.load-overlay {
  z-index: 10;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  .content {
    @include center-in-parent;
    color: white;
    font-family: $font-body;
    font-size: $font-size-xxs;
    text-transform: uppercase;
    pointer-events: all;
    .text {
      margin-top: $space-m;
      opacity: 0;
      transition: .3s all;

      &.visible {
        opacity: 1;
        transition: .3s all;
      }
    }
    p {
      margin: 0;
      white-space: nowrap;
    }
  }
}

// STORY SPECIFIC
.college-football .load-overlay {
  p.reload {
    color: $brand-yellow;
  }
}

.studio-shows .load-overlay {
  p.reload {
    color: $ss-orange;
  }
}

/***
 *             _________
 *    |\     /|\__   __/
 *    | )   ( |   ) (   
 *    | |   | |   | |   
 *    | |   | |   | |   
 *    | |   | |   | |   
 *    | (___) |___) (___
 *    (_______)\_______/
 *                      
 */
/* Contains various UI elements, such as arrows, progress bar, pause & play button */
.ui-container {
  z-index: $z-story-ui;
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  text-align: center;
  color: $brand-grey-transparent;

  .max-container {
    max-width: $progress-bar-max;
    margin: 0 auto;
  }

  .slide-title {
    position: fixed;
    top: $space-ml;
    left: $space-xxl;

    font-family: $font-body;
    font-weight: normal;
    font-size: $font-size-xxs;
    text-transform: uppercase;
    transform: translateY(2px);
    transition: 0.3s all;

    &.hidden {
      opacity: 0;
      transition: 0.3s all;
    }

    @include media($bp-tablet) {
      top: $space-s;
      left: #{$space-m + $space-s * 2};
      font-size: $font-size-xxs;
    }

    @include media($bp-phone-l) {
      top: 15px;
      left: #{20px + $space-s * 2};
      font-size: $font-size-xxxs;
    }
  }

  .section-widget {
    position: fixed;
    top: $space-ml;
    right: $space-ml;
    width: 40px;
    opacity: 0;
    transition: .3s opacity;

    &.visible {
      opacity: 1;
      transition: .3s opacity;
    }

    @include media($bp-tablet) {
      top: $space-s;
      right: $space-s;
    }
    @include media($bp-phone-l) {
      width: 20px;
    }
  }

  .persistent-section-title {
    position: fixed;
    top: $space-ml;
    right: $space-ml;
    font-family: inherit;

    @include media($bp-tablet) {
      top: $space-s;
      right: $space-s;
    }
    @include media($bp-phone-l) {
      top: $space-xs;
      font-size: 21px;
    }
  }

  .current-section {
    pointer-events: none;
    display: none;
    position: fixed;
    bottom: 36px;
    left: 0;
    transform: translateY(-50%);
    width: 100%;
    opacity: 1;
    transition: .3s opacity;

    &.hidden {
      opacity: 0;
      transition: .3s opacity;
    }

    @include media($bp-phone-l) {
      display: block;
    }
  }

  .section-titles {
    font-family: $font-body;
    transition: .3s color;
    &.hidden {
      opacity: 0;
      pointer-events: none;
      white-space: nowrap;
    }
    h4 {
      display: inline-block;
    }

    @include media-wh($bp-phone-l, $hbp-phone-l) {
      display: none;
    }
  }
}

.dark-theme .ui-container,
.white-ui .ui-container {
  color: white;
}

.audio-notification {
  pointer-events: none;
  opacity: 0;
  cursor: pointer;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 400px;
  height: 400px;
  transition: .3s all;

  &.visible {
    pointer-events: all;
    opacity: 1;
  }

  .circle {
    @include center-in-parent;
    animation: audioPulse 6s infinite linear;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background-color: white;
  }

  .content {
    @include center-in-parent;
    transform: translate(-50%, -40%)
  }

  p {
    margin: 0;
    font-family: $font-body;
    color: $brand-dark;
  }

  button {
    text-transform: uppercase;
    font-family: $font-display;
    font-size: $font-size-s;
    padding: 4px $space-m;
    margin-top: $space-ms;
    border-radius: 8px;

    span {
      display: inline-block;
      transform: translateY(-2px);
    }
  }
}

// STORY SPECIFIC
.college-football .audio-notification {
  p {
    @include highlight;
    text-transform: uppercase;
  }
  button {
    color: white;
    background-color: $brand-dark;
  }
}

.studio-shows .audio-notification,
.nascar .audio-notification {
  .content {
    width: 100%;
  }
  p {
    color: white;
    text-transform: lowercase;
    font-family: $font-title-regular;
    font-size: $font-size-ms;
    span {
      font-family: $font-title-heavy;
    }
  }
  .circle {
    background-color: $ss-orange;
  }
  button {
    color: $brand-dark;
    background-color: white;
  }
}

.story-arrows {
  @include fill-screen;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  pointer-events: none;

  &.minimal {
    .story-arrow .hitbox {
      pointer-events: none;
    }
  }

  .story-arrow {
    @include fill-screen;
    cursor: pointer;
    position: absolute;
    top: 0;
    transition: 0.3s all;
    width: 50%;

    .hitbox {
      position: absolute;
      top: 0;
      left: 0;
      height: calc(100% - 145px);
      width: 100%;
      pointer-events: all;

      @include media($bp-phone-l) {
        height: calc(100% - 105px);
      }
    }

    img {
      position: absolute;
      height: $space-m;
      bottom: $space-ml;
      transform: translateY(-6px);
      pointer-events: all;

      @include media($bp-desktop-s) {
        bottom: $space-xl;
      }
      @include media-wh($bp-tablet, $hbp-tablet) {
        bottom: #{36px + $space-s * 2};
        transform: translateY(3px);
      }

      @include media-wh($bp-phone-l, $hbp-phone-l) {
        height: 28px;
        bottom: #{$space-s * 2 + 3px}
      }
    }
  }

  .next-arrow {
    right: 0;

    &:hover {
      transform: translateX(4px);
      transition: 0.3s all;
    }

    // Before the story has started, the right arrow covers the screen to ensure the user presses it
    // (This is an iOS fix)
    &.fullWidth {
      width: 100%;
      z-index: 1;
    }

    img {
      right: $space-ml;
      @include media-wh($bp-tablet, $hbp-tablet) {
        right: $space-s;
      }
    }
  }
  .prev-arrow {
    left: 0;

    &:hover {
      transform: translateX(-4px);
      transition: 0.3s all;
    }

    img {
      left: $space-ml;
      @include media-wh($bp-tablet, $hbp-tablet) {
        left: $space-s;
      }
    }
  }
}

.play-button {
  position: absolute;
  bottom: $space-m;
  left: 120px;
  height: $space-ml;
  cursor: pointer;
  transform: translateY(-6px);

  img {
    height: $space-m;
  }

  @include media($bp-desktop-s) {
    bottom: $space-xl;
    transform: none;
  }

  @include media-wh($bp-tablet, $hbp-tablet) {
    bottom: #{36px + $space-s * 2};
    left: #{40px + $space-s * 2};
    height: 32px;

    img {
      width: 32px;
      height: 32px;
    }
  }

  @include media-height($bp-tablet) {
    transform: none;
  }

  @include media-wh($bp-phone-l, $hbp-phone-l) {
    height: 25px;
    transform: none;
    bottom: #{$space-s * 2 + 5px};
    left: #{$space-s * 2 + 18px};
    img {
      width: 25px;
    }
  }
}

.story-progress-bar {
  width: 100%;
  max-width: $progress-bar-max;
  margin: 0 auto $space-m;
  font-size: $font-size-xxxs;
  font-weight: normal;
  text-transform: uppercase;

  @include media($bp-desktop-xl) {
    max-width: 100%;
    padding: 0 214px;
  }

  @include media($bp-desktop-s) {
    padding: 0 $space-ml;
  }

  @include media-wh($bp-tablet, $hbp-tablet) {
    margin: 0 auto $space-s;
    padding: 0 $space-s;
  }

  @include media-wh($bp-phone-l, $hbp-phone-l) {
    padding: 0 $space-s;
    margin: 0 auto $space-s;
  }

  .loaders-container {
    font-size: 0;
    margin: $space-s 0;
  }

  .loader-container {
    display: inline-block;
    padding: 0 4px;
    height: 3px;

    @include media($bp-tablet) {
      padding: 0 3px;
    }

    @include media($bp-phone-l) {
      padding: 0 2px;
    }

    &:first-child {
      padding-left: 0;
    }
    &:last-child {
      padding-right: 0;
    }
  }

  button {
    position: relative;
    display: inline-block;
    width: 100%;
    height: 3px;
    background-color: $brand-grey-transparent;
    border: none;
    box-shadow: $shadow-tiny;
    transition: .3s background-color;

    .loader {
      background-color: $brand-yellow-transparent;
      position: absolute;
      top: 0;
      left: 0;
      width: 0%;
      height: 100%;
      transition: .3s background-color;
    }
  }
}

.dark-theme {
  .story-progress-bar {
    button {
      background-color: $white-transparent;
    }
  }
  .story-nav .story-outline .active::before {
    border-left-color: white;
  }
}

// STORY SPECIFIC
body.studio-shows {
  .story-progress-bar .loader {
    background-color: $ss-orange;
  }
}

/***
 *     ______   _______  _______ _________ _______ 
 *    (  ___ \ (  ____ \(  ___  )\__   __/(  ____ \
 *    | (   ) )| (    \/| (   ) |   ) (   | (    \/
 *    | (__/ / | (__    | (___) |   | |   | (_____ 
 *    |  __ (  |  __)   |  ___  |   | |   (_____  )
 *    | (  \ \ | (      | (   ) |   | |         ) |
 *    | )___) )| (____/\| )   ( |   | |   /\____) |
 *    |/ \___/ (_______/|/     \|   )_(   \_______)
 *                                                 
 */
/* Contains styling for various story beat classes */
.story-beat {
  @include bg-cover-center;
  @include fill-screen;

  position: relative;
  overflow: hidden;

  &:focus {
    outline: none;
  }

  &.thesis {
    .feature-animation {
      @include center-in-parent;
      width: 100%;
      max-width: 1280px;
    }
  }

  &.section-title {
    .section-parts {
      @include fill-screen;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;

      .layers {
        position: relative;
        height: 100%;
        overflow: hidden;
        > div {
          @include center-in-parent;
          @include bg-cover-center;

          height: 100%;
          width: 100%;
          transition: 1s all;

          &.middle {
            transform: translate(-47%, -50%);
            z-index: 2;
          }
          &.front {
            z-index: 2;
          }
        }
        &.moving {
          .middle {
            transform: translate(-50%, -50%);
          }
        }
      }
    }
    // Exception for "The Future" section cover
    &.future {
      .section-parts .layers {
        .front {
          @media (max-aspect-ratio: 1/1) {
            transform: translate(-50%, -48.8%);
          }
          @include media($bp-phone-l) {
            z-index: 5;
          }
        }
      }
    }
  }

  &.text {
    font-size: $font-size-mml;
    line-height: 110%;

    @include media($bp-tablet) {
      font-size: $font-size-m;
      padding: 0 $space-ms;
    }
    @include media($bp-phone-l) {
      font-size: $font-size-s;
      line-height: 140%;
    }

    .text-container {
      .copy-segment {
        @include center-in-parent;
        opacity: 0;
        width: 100%;
        max-width: $bp-desktop-l;
        position: absolute;
        text-align: center;
        transition: 0.3s all;
        padding: 0 $space-m;

        &.shown {
          opacity: 1;
          transition: 0.3s all;
        }
      }
      .animated-text {
      }
    }
  }

  &.image {
    .container {
      height: -webkit-fill-available;
      background-size: cover;
      background-position: center center;
    }
    .image-container {
      @include center-in-parent;
      width: 100%;
      max-width: $bp-desktop-s;

      img {
        width: 100%;
        margin: 0 auto;
        
        @media (max-aspect-ratio: 1/1) {
          width: 80%;
        }
        @include media($bp-phone-l) {
          width: 90%;
        }
      }
    }
  }

  &.video {
    .video-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: $overlay;
      z-index: 1;
      opacity: 1;
      transition: .3s all;

      .text-container {
        @include center-in-parent;
        background-color: white;
        color: black;
        text-align: center;
        padding: $space-ms;
        width: 100%;
        max-width: 720px;
        border-radius: $space-xs;

        h2 {
          text-transform: uppercase;
          font-size: $font-size-s;
          line-height: 1.2;
          font-family: $font-title-heavy;
          padding: $space-ms 0;
          border-bottom: solid 1px $ss-orange;
        }
        p {
          font-family: $font-title-regular;
          font-size: $font-size-s;
          line-height: 1.3;
          padding: $space-ms;
        }

        @include media($bp-tablet) {
          padding: $space-s;
          width: calc(100% - #{$space-l} * 2);
          h2 {
            font-size: $font-size-xs;
            padding: $space-s 0;
          }
          p {
            font-size: $font-size-xs;
            padding: $space-s 0;
          }
        }

        @include media($bp-phone-m) {
          width: calc(100% - #{$space-ml} * 2);
          padding: 0 $space-s;
          min-height: 260px;
          h2 {
            font-size: $font-size-xxs;
          }
          p {
            font-size: $font-size-xxs;
          }
        }
      }

      &.hidden {
        opacity: 0;
        transition: 0.3s all;
      }
    }
  }

  &.infographic {
    background-color: white;
    .side-by-side {
      height: 100%;

      > div {
        height: 100%;
        position: relative;
        display: inline-block;
        width: 50%;
        vertical-align: top;

        @include media($bp-desktop-s) {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          padding: 0 #{$space-xxl};
        }

        @include media($bp-tablet) {
          padding: 0 #{$space-m + $space-s * 2};
        }

        .infographic-container {
          height: 90%;
          margin-top: 5%;
        }
      }
      .left {
        z-index: 1;
        padding-right: $space-l;
        @include media($bp-desktop-l) {
          padding-right: $space-ml;
        }
        @include media($bp-desktop-s) {
          padding: 0 #{$space-xxl};
          &.hidden {
            opacity: 0;
            pointer-events: none;
            transition: 0.5s all;
          }
        }
        @include media($bp-tablet) {
          padding: 0 #{$space-m + $space-s * 2};
        }
        @include media($bp-phone-l) {
          padding: 0 #{$space-s + 20px};
        }

        .copy {
          margin-top: calc(#{$space-ml} * 2);
          font-family: $font-title;
          font-size: $font-size-ms;
          opacity: 0;
          transition: .5s all;

          &.visible {
            opacity: 1;
            transition: .5s all;
          }

          @include media($bp-desktop-xxl) {
            font-size: 22px;
          }

          @include media($bp-desktop-xl) {
            font-size: $font-size-s;
          }

          @include media($bp-desktop-l) {
            font-size: 18px;
          }

          @include media(1100px) {
            font-size: $font-size-xs;
          }

          @include media($bp-phone-l) {
            margin-top: #{$space-ms * 2};
          }

          @include media-height($hbp-tablet) {
            font-size: $font-size-xs;
          }

          @include media-height($hbp-phone-l) {
            font-size: $font-size-xxs;
          }

          // individual copy segments
          .copy-segment {
            display: inline;

            @include media($bp-phone-m) {
              position: absolute;
              width: calc(100% - #{($space-s + 20px) * 2});
              opacity: 0;
              transition: 0.3s all;
              &.shown {
                opacity: 1;
                transition: 0.3s all;
              }
            }
          }
        }
      }

      .right {
        transform: translateX(100px);
        @include media($bp-desktop-s) {
          transform: none;
        }
        @include media($bp-tablet) {
          padding: $space-s;
        }
        @include media($bp-phone-l) {
          padding: 0;
        }
      }
    }
  }

  &.editorial .container,
  &.text-and-media .container {
    @include media($bp-desktop-s) {
      padding: 0 $space-ml;
    }
    @include media($bp-tablet) {
      padding: 0 $space-s;
    }
  }

  &.infographic-full {
    height: 100%;

    .container {
      padding: 0;
    }

    .infographic-full-container {
      height: 100%;
      position: relative;
    }

    .feature-animation {
      height: 100%;
      max-height: -webkit-fill-available;

      @include media($bp-phone-s) {
        transform: translateY(-1.5vh);
      }
    }

    .copy {
      position: fixed;
      z-index: $z-content;
      pointer-events: none;
      bottom: 145px;
      left: 50vw;
      transform: translateX(-50%);
      width: 100vw;
      max-width: 800px;
      box-sizing: border-box;
      padding: 0 $space-m;
      font-size: $font-size-m;
      font-family: $font-title-regular;
      text-align: center;

      @include media($bp-desktop-s) {
        width: 65vw;
        bottom: 115px;
        font-size: $font-size-ms;
      }

      @include media-height($hbp-desktop-s) {
        bottom: 100px;
      }

      @include media($bp-tablet) {
        font-size: $font-size-xs;
        max-width: $bp-phone-l;
        // bottom: 145px;
        bottom: 80px;
      }
      @include media($bp-phone-l) {
        font-size: $font-size-xs;
        bottom: 80px;
        width: 100vw;
      }
    }

    &.limit-height {
      .infographic-full-container {
        max-height: calc(100% - 84px);
      }
    }

    &.top-twenty {
      .copy {
        max-width: $bp-desktop-l;
      }
    }
  }

  &.quote {
    background-color: $brand-light;
    
    .container {
      @include media($bp-desktop-xxl) {
        padding: 0 $space-ml;
      }
      @include media($bp-tablet) {
        padding: 0 $space-s;
      }
    }

    .quote-container {
      @include media($bp-desktop-xl) {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -46%);
        width: 100%;
      }
      
      .vid-container {
        position: relative;
        width: 100%;
        height: 740px;
        max-height: 60vh;
        margin-top: $space-l;

        @include media(1100px) {
          max-height: 40vw;
        }

        @media (max-aspect-ratio: 1/1) {
          height: 620px;
          max-height: 60vh;
        }
        @include media($bp-phone-l) {
          margin-top: $space-m;
        }
      }
    }

    video {
      @include center-in-parent;
      max-width: 130vw;
      max-height: 340%;

      @media (max-aspect-ratio: 1/1) {
        max-width: 1024px;
        max-height: 280%;
      }

      @include media($bp-tablet) {
        max-width: 1024px;
        max-height: 200%;
      }

      @include media(380px) {
        max-width: 230vw;
      }
    }

    .quote-lower-third {
      width: 20vw;
      max-width: 500px;
      min-width: 400px;
      margin: 0 auto;
      transform: translateY(-#{$space-s});

      @include media($bp-tablet) {
        min-width: 300px;
      }
      @include media($bp-phone-l) {
        min-width: 255px;
      }
    }

    &.gaby {
      .quote-lower-third {
        transform: translateY(-56px);

        @media (max-aspect-ratio: 1/1) {
          transform: translateY(-#{$space-m});
        }

        @include media($bp-phone-l) {
          transform: none;
        }
      }
    }
  }

  &.testimonial {
    .lower-third-container {
      position: absolute;
      bottom: #{$space-s};
      left: 0;
      width: 40vw;
      max-width: 800px;
      min-width: 600px;

      > div {
        height: 100%;
      }
      @include media($bp-tablet) {
        left: -50px;
        bottom: -#{$space-s};
        min-width: 560px;
      }
      @include media($bp-phone-l) {
        left: -100px;
        min-width: 520px;
        bottom: -#{$space-ml};
      }
      @include media($bp-phone-m) {
        left: -130px;
      }
    }

    @include media($bp-phone-l) {
      &.doug .lower-third-container {
        bottom: -12px;
      }
      &.reese .lower-third-container {
        bottom: -30px;
      }
      &.alex .lower-third-container {
        bottom: -36px;
      }
    }
  }

  &.question {
    .container {
      background-size: cover;
      background-position: center center;

      @include media($bp-desktop-l) {
        padding: 0 $space-l;
      }
      @include media($bp-desktop-s) {
        padding: 0 $space-ml;
      }
      @include media($bp-phone-l) {
        padding: 0 $space-s;
      }
    }
    .question-container {
      position: relative;
      height: 100%;
      height: -webkit-fill-available;

      &.answered {
        .split {
          .left {
            transform: translateX(0);
            transition: .5s transform;

            @include media-wh ($bp-tablet, $hbp-phone-l) {
              transform: translateY(-50%);
              opacity: 0;
              transition: .5s opacity;
              pointer-events: none;
            }
          }
          .right {
            opacity: 1;
            transition: .5s opacity;

            @include media-wh ($bp-tablet, $hbp-phone-l) {
              transform: translateY(-50%);
              margin-left: 0;
            }
          }
        }
      }

      .split {
        font-size: 20px;
        font-family: $font-title-betting;
        position: absolute;
        top: 45%;
        transform: translateY(-50%);
        width: 100%;

        @include media($bp-desktop-l) {
          font-size: $font-size-xs;
        }

        @include media($bp-desktop-s) {
          font-size: $font-size-xxs;
        }

        .left, .right {
          vertical-align: top;
          display: inline-block;
          width: calc(50% - #{$space-m});

          @include media($bp-desktop-l) {
            width: calc(50% - #{$space-s})
          }

          @include media-wh($bp-tablet, $hbp-phone-l) {
            position: absolute;
            top: 0;
            transform: translateY(-50%);
            width: 100%;
          }

          h2 {
            font-size: 40px;
          }

          .header-image {
            display: block;
            margin-bottom: $space-ms;

            @include media-height($hbp-phone-l) {
              margin-bottom: $space-s;
              height: 20px;
            }
          }

          p.question {
            font-size: $font-size-ms;
            line-height: 120%;
            font-family: $font-title-betting;

            @include media ($bp-desktop-s) {
              font-size: $font-size-s;
            }
          }
        }
        .left {
          transform: translateX(calc(50% + #{$space-m}));
          margin-right: $space-m;

          @include media($bp-desktop-l) {
            transform: translateX(calc(50% + #{$space-s}));
            margin-right: $space-s;
          }

          @include media-wh($bp-tablet, $hbp-phone-l) {
            transform: translateY(-50%);
          }

          .question-render {
            width: auto;
            max-width: 100%;
            margin-bottom: $space-m;

            @include media($bp-desktop-l) {
              margin-bottom: $space-s;
            }
          }

          .question-image {
            width: auto;
            max-width: 100%;
            margin-bottom: $space-s;

            @include media-height($bp-phone-l) {
              max-height: 50px;
              width: auto;
            }
          }

          .answers {
            position: relative;

            button {
              border-radius: 5px;
              padding: $space-xs $space-s;

              &:hover {
                background-color: grey;
              }

              @include media-height($bp-phone-l) {
                position: absolute;
                bottom: 0;
                right: 0;
              }
            }
            ol {
              margin-bottom: $space-m;

              @include media($bp-desktop-l) {
                margin-bottom: $space-s;
              }
            }
            li {
              cursor: pointer;
              line-height: 1.3em;
              margin-bottom: $space-s;
              margin-left: $space-m;
              transition: .3s color;

              @include media($bp-desktop-s) {
                margin-left: 0;
              }

              @include media-wh($bp-tablet, $hbp-tablet) {
                margin-bottom: $space-xs;
              }

              @include media-height($hbp-phone-l) {
                margin-bottom: 0;
              }
              
              &:hover {
                color: grey;
              }
              &.selected {
                color: grey;
              }
              &.correct {
                color: $correct;
              }
              &.incorrect {
                color: $incorrect;
              }
            }
          }
        }
        .right {
          margin-left: $space-m;
          opacity: 0;
          pointer-events: none;

          @include media($bp-desktop-l) {
            margin-left: $space-s;
          }

          .results-list {
            list-style: none;
            padding: 0;
            padding-top: $space-l;
            font-size: $font-size-ms;
            @include media-wh($bp-phone-l, $hbp-phone-l) {
              font-size: $font-size-s;
            }
            @include media-wh($bp-phone-l, $hbp-tablet) {
              padding-top: $space-m;
            }
            li {
              margin-bottom: $space-s;
            }
          }

          table {
            width: 100%;
            tr {
              td:nth-child(1) {
                text-align: left;
                font-size: inherit
              }
              th {
                text-transform: uppercase;
              }
              td {
                font-size: $font-size-m;
              }
              th, td {
                padding: $space-s $space-s $space-s 0;
                text-align: center;

                @include media-wh($bp-phone-l, $hbp-phone-l) {
                  padding: $space-xs $space-xs $space-xs 0;
                }
              }
            }
          }
        }
      }
    }
  }

  .container {
    max-width: $max-width;
    height: 100%;
    margin: 0 auto;
    padding: 0 $space-xxl;
  }

  video.fullscreen {
    @include center-in-parent;
    background-color: black;
    height: 100.5%;
    width: 177.77777778vh; /* 100 * 16 / 9 */
    min-width: 100%;
    min-height: 56.25vw; /* 100 * 9 / 16 */

    // Different aspect ratios
    &.mobile {
      width: 56.33803vh;
      min-height: 177.5vw;
    }

    &.letterbox {
      width: 100vw;
    }

    &.ultrawide {
      width: 237.038vh;
      min-height: 42.1875vw;
    }

    &.faded {
      opacity: 0 !important;
      transition: 0.5s all;
    }
  }

  .overlay-video-container {
    @include center-in-parent;
    width: calc(100% - #{$space-xl} * 2);
    height: calc(100% - #{$space-xl} * 2);
    overflow: hidden;

    @include media($bp-tablet) {
      width: calc(100% - #{$space-l} * 2);
      height: calc(100% - #{$space-l} * 2);
    }
    @include media($bp-phone-l) {
      width: calc(100% - #{$space-ml} * 2);
      height: calc(100% - #{$space-ml} * 2);
    }
  }
  .overlay-video {
    @include center-in-parent;
    height: 100%;
    width: 177.77777778vh;
    min-width: 100%;
    min-height: 56.25vw;
  }

  .background-cover {
    @include fill-parent;
    pointer-events: none;
  }
}

// STORY SPECIFIC
.college-football .story-beat {
  &.section-title {
    .section-parts {
      &.right {
        .layers {
          .middle,
          .front {
            @include media(1600px) {
              background-position: 22% center;
            }
            @include media(1280px) {
              background-position:18% center;
            }
            @media (max-aspect-ratio: 1/1) {
              background-position: 30% center;
            }
            @include media($bp-phone-m) {
              background-position: 50% center;
            }
          }

          @include media($bp-phone-m) {
            .middle {
              z-index: 5;
            }
            .front {
              z-index: 6;
            }
          }
        }
      }
    }
    .title {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      img {
        position: absolute;
        width: 700px;
        margin: #{$space-ml + 5px};
        &.rip-full {
          z-index: 1;
        }
        &.rip-partial {
          z-index: 4;
        }

        @include media($bp-tablet) {
          width: 110%;
          max-width: 700px;
          min-width: 475px;
          margin: $space-ms;
        }

        @include media($bp-phone-l) {
          width: 104%;
          margin: 20px 26px;
        }
      }

      h1 {
        position: absolute;
        top: calc(#{$space-ml + $space-s} + 14px);
        left: calc(#{$space-ml + $space-s} + 32px);
        letter-spacing: .15em;
        z-index: 5;

        @include media($bp-tablet) {
          top: 60px;
          left: 60px;
          font-size: 35px;
        }

        @include media($bp-phone-l) {
          top: 36px;
          left: 42px;
          font-size: 28px;
          z-index: 4;
        }
      }
    }
  }
}

body.studio-shows .story-beat {
  &.section-title {
    background-color: $ss-purple-covers;
    .section-parts .layers > div {
      &.middle {
        background-size: auto 50%;
        background-position: calc(100% - 80px) 80px;

        @include media ($bp-desktop-s) {
          background-size: auto 300px;
          background-position: calc(100% - 40px) 40px;
        }
        @include media ($bp-phone-l) {
          background-position: calc(100% - 20px) 20px;
          background-size: auto 220px;
        }
        @include media($bp-phone-s) {
          background-size: auto 200px;
        }
      }
    }
    .title {
      @include center-in-parent;
      width: 100%;
      padding: 0 $space-m;
      box-sizing: border-box;
      z-index: 5;

      h1 {
        font-family: $font-title-regular;
        text-align: center;
        font-size: 80px;
        font-weight: 300;
        line-height: 100%;

        @include media($bp-tablet) {
          font-size: $font-size-mml;
        }
        @include media($bp-phone-l) {
          font-size: $font-size-ml;
        }
        @include media($bp-phone-m) {
          font-size: $font-size-m;
        }
      }
    }
  }

  &.text {
    .text-container {
      font-family: $font-title-regular;
      font-weight: 300;

      .copy-segment {
        letter-spacing: -0.04em;
      }
    }
  }
}

.bg-vid-cover {
  @include center-in-parent;
  opacity: 0;
  width: 100%;
  height: 100vh;
  height: -webkit-fill-available;

  pointer-events: none;

  transition: .3s all;
  background-size: cover;
  background-position: center center;

  &.visible {
    opacity: 1;
    transition: .3s all;
  }
}
</style>
